import React, { useEffect, useState } from "react";
import { Copy, Gift, Users2, Share2, Mail, MessageCircle, Twitter } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function Referrals() {
  const [ref, setRef] = useState(null);

  useEffect(() => {
    api.get("/referrals/me").then(r => setRef(r.data)).catch(() => {});
  }, []);

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied!"); }
    catch { toast.error("Copy failed"); }
  };

  const shareText = ref
    ? `I've been using Treasurescare to protect my phone — AI-powered claim assessment, 24h repair. Sign up with my code ${ref.code} and we both get a free month: ${ref.share_url}`
    : "";

  const shareVia = (channel) => {
    if (!ref) return;
    const enc = encodeURIComponent(shareText);
    const url = encodeURIComponent(ref.share_url);
    const links = {
      whatsapp: `https://wa.me/?text=${enc}`,
      email: `mailto:?subject=${encodeURIComponent("Get a free month of Treasurescare")}&body=${enc}`,
      x: `https://x.com/intent/tweet?text=${enc}&url=${url}`,
    };
    if (links[channel]) window.open(links[channel], "_blank", "noopener,noreferrer");
  };

  const nativeShare = async () => {
    if (!ref) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Treasurescare — Device Protection",
          text: shareText,
          url: ref.share_url,
        });
      } catch (err) {
        if (err?.name !== "AbortError") console.debug("share failed:", err);
      }
    } else {
      copy(ref.share_url);
    }
  };

  return (
    <div className="space-y-8 max-w-3xl" data-testid="referrals-page">
      <div>
        <div className="overline text-secondary">Loyalty</div>
        <h1 className="mt-1 font-display text-4xl font-light tracking-tight">Give a month, get a month.</h1>
        <p className="mt-2 text-muted-foreground">
          Share your code — anyone who subscribes gives <strong>you 1 free month</strong>, and gets a
          <strong> warm £10-off welcome</strong> themselves. Unlimited invites.
        </p>
      </div>

      <div className="bg-primary text-primary-foreground rounded-2xl p-8 shadow-teal-glow relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-40" />
        <div className="relative grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <div className="overline text-secondary">Your code</div>
            <div className="mt-2 font-display text-4xl tracking-tight" data-testid="referral-code">{ref?.code || "…"}</div>
          </div>
          <div>
            <div className="overline text-secondary flex items-center gap-2"><Users2 className="w-3 h-3" /> Friends joined</div>
            <div className="mt-2 font-display text-4xl">{ref?.referred_count ?? "…"}</div>
          </div>
          <div>
            <div className="overline text-secondary flex items-center gap-2"><Gift className="w-3 h-3" /> Free months earned</div>
            <div className="mt-2 font-display text-4xl">{ref?.credits_months ?? "…"}</div>
          </div>
        </div>
      </div>

      <div className="bg-card border border-border rounded-2xl p-6">
        <div className="overline text-secondary">Share your link</div>
        <div className="mt-4 flex flex-col sm:flex-row gap-2">
          <Input readOnly value={ref?.share_url || ""} className="font-mono text-xs" data-testid="referral-link-input" />
          <Button variant="outline" onClick={() => copy(ref?.share_url || "")} data-testid="referral-copy-btn">
            <Copy className="w-4 h-4 mr-2" /> Copy
          </Button>
          <Button onClick={nativeShare} data-testid="referral-share-btn">
            <Share2 className="w-4 h-4 mr-2" /> Share
          </Button>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => shareVia("whatsapp")} data-testid="referral-whatsapp-btn" className="border-primary/30 text-primary">
            <MessageCircle className="w-3.5 h-3.5 mr-1.5" /> WhatsApp
          </Button>
          <Button variant="outline" size="sm" onClick={() => shareVia("email")} data-testid="referral-email-btn">
            <Mail className="w-3.5 h-3.5 mr-1.5" /> Email
          </Button>
          <Button variant="outline" size="sm" onClick={() => shareVia("x")} data-testid="referral-x-btn">
            <Twitter className="w-3.5 h-3.5 mr-1.5" /> X (Twitter)
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { i: 1, title: "Share your code", body: "Send your unique link to friends and colleagues." },
          { i: 2, title: "They subscribe", body: "New members use your code to unlock £10 off their first month." },
          { i: 3, title: "You earn", body: "1 free month credited to you for every successful referral." },
        ].map(s => (
          <div key={s.i} className="bg-card border border-border rounded-2xl p-6">
            <div className="w-8 h-8 rounded-full bg-secondary/20 text-secondary font-semibold flex items-center justify-center">{s.i}</div>
            <div className="mt-3 font-display text-lg">{s.title}</div>
            <div className="mt-1 text-sm text-muted-foreground">{s.body}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
